import styled from "@emotion/styled";
import React, { RefObject, useEffect } from "react";
import { TAB_BAR_HEIGHT } from "@components/MobileTabBar";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { CHATWOOT_LAUNCHER_ID, toggle as toggleChatwoot } from "@src/utils/chatwoot";

const LAUNCHER_SIZE = 60;
const LAUNCHER_RIGHT = 20;
const LAUNCHER_BOTTOM = 20;
/** Space left between the launcher and a bottom control it has moved above. */
const CLEARANCE_GAP = 12;
const LIFT_VAR = "--chat-launcher-lift";

/** Every registered control's lift; the launcher clears the tallest. */
const lifts = new Map<symbol, number>();
const applyLift = () => {
  const lift = Math.max(0, ...Array.from(lifts.values()));
  document.documentElement.style.setProperty(LIFT_VAR, `${lift}px`);
};

/**
 * Keeps the desktop launcher above a control pinned to the bottom of the
 * window, such as the satellite date strip. The compare pane's strip runs to
 * the right edge, where the launcher sits, and covered its last dates. The
 * launcher moves up only while the control actually reaches its corner,
 * measured against the launcher's resting spot so the lift cannot feed back
 * into the check. Mobile places the launcher above the tab bar and ignores it.
 */
export const useChatLauncherClearance = (ref: RefObject<HTMLElement>) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const key = Symbol("chat-launcher-clearance");
    const update = () => {
      const r = el.getBoundingClientRect();
      const launcherLeft = window.innerWidth - LAUNCHER_RIGHT - LAUNCHER_SIZE;
      const launcherTop = window.innerHeight - LAUNCHER_BOTTOM - LAUNCHER_SIZE;
      const covers = r.width > 0 && r.right > launcherLeft && r.bottom > launcherTop;
      lifts.set(key, covers ? window.innerHeight - r.top + CLEARANCE_GAP - LAUNCHER_BOTTOM : 0);
      applyLift();
    };
    // The control's own size (more dates loaded) and its container's (split
    // view opening, the sidebar collapsing) both move its edges.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener("resize", update);
    update();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      lifts.delete(key);
      applyLift();
    };
  }, [ref]);
};

const LauncherButton = styled.button`
  position: fixed;
  right: ${LAUNCHER_RIGHT}px;
  bottom: calc(${LAUNCHER_BOTTOM}px + var(${LIFT_VAR}, 0px));
  width: ${LAUNCHER_SIZE}px;
  height: ${LAUNCHER_SIZE}px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #00d4aa 0%, #00a86b 100%);
  box-shadow: 0 4px 20px rgba(0, 212, 170, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  transition: transform 0.2s, box-shadow 0.2s, bottom 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 24px rgba(0, 212, 170, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  /* Mobile: position above the tab bar (~106px from bottom) + padding */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    right: max(16px, env(safe-area-inset-right, 16px));
    bottom: calc(
      ${TAB_BAR_HEIGHT}px + var(--tabbar-inset, env(safe-area-inset-bottom, 0px)) + 16px
    );
  }
`;

const ChatIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M14 4C8.477 4 4 7.82 4 12.5c0 2.89 1.56 5.47 4 7.24v3.76l3.65-2.19c.98.16 2.02.25 3.11.25 5.52 0 10-3.82 10-8.5S19.52 4 14 4z"
      fill="white"
    />
  </svg>
);

const ChatwootLauncher: React.FC = () => {
  return (
    <LauncherButton
      id={CHATWOOT_LAUNCHER_ID}
      type="button"
      aria-label="Open support chat"
      onClick={() => toggleChatwoot("open")}
    >
      <ChatIcon />
    </LauncherButton>
  );
};

export default ChatwootLauncher;
